import { useEffect, useMemo, useRef, useState } from "react";
import {
  BarChart3,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FilePenLine,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
  UploadCloud
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import * as XLSX from "xlsx";
import {
  buildFinancialDatasetRows,
  createEmptyFinancialReportData,
  createFinancialPivot,
  FINANCIAL_DATASETS,
  FINANCIAL_DIMENSION_LABELS,
  formatFinancialPeriod,
  normalizeFinancialText,
  recalculateFinancialReportData,
  readMonthlyFinancialFile
} from "../helpers/monthlyFinancialReport";
import {
  deleteMonthlyFinancialReport,
  saveMonthlyFinancialReport,
  subscribeMonthlyFinancialReports
} from "../services/monthlyFinancialReportService";

const CHART_COLORS = ["#0f766e", "#2563eb", "#d97706", "#7c3aed", "#dc2626", "#0891b2"];

const money = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
  maximumFractionDigits: 2
});

const compactMoney = new Intl.NumberFormat("tr-TR", {
  notation: "compact",
  maximumFractionDigits: 1
});

const normalizeCompanies = (companyOptions, userData) => {
  const items = (companyOptions || []).map((company) => ({
    id: company.id || normalizeFinancialText(company.firmaAdi || company.name).replace(/\s+/g, "-"),
    name: company.firmaAdi || company.legalName || company.name || "",
    shortName: company.firmaKisaAdi || company.shortName || company.firmaAdi || company.name || ""
  })).filter((company) => company.id && company.name);

  const assigned = Array.isArray(userData?.assignedCompanies)
    ? userData.assignedCompanies.map((item) => normalizeFinancialText(item))
    : [];
  if (!assigned.length) return items;

  return items.filter((company) =>
    assigned.includes(normalizeFinancialText(company.id)) ||
    assigned.includes(normalizeFinancialText(company.name)) ||
    assigned.includes(normalizeFinancialText(company.shortName))
  );
};

const normalizeProjects = (projectOptions, company) => {
  if (!company) return [];
  const companyId = String(company.id || "");
  const companyName = normalizeFinancialText(company.name);
  const companyShortName = normalizeFinancialText(company.shortName);

  return (projectOptions || [])
    .filter((project) => {
      if (project.isArchived) return false;
      return String(project.companyId || "") === companyId ||
        normalizeFinancialText(project.firmaAdi) === companyName ||
        normalizeFinancialText(project.firmaKisaAdi) === companyShortName;
    })
    .map((project) => ({
      id: String(project.id || `${project.projeStbKodu || ""}-${project.projeAdi || ""}`),
      code: String(project.projeStbKodu || project.projectCode || project.code || "").trim(),
      name: String(project.projeAdi || project.projectName || project.name || "").trim(),
      status: String(project.projeDurumu || project.status || "Devam Ediyor").trim()
    }))
    .filter((project) => project.id && project.name)
    .sort((left, right) => left.name.localeCompare(right.name, "tr"));
};

const latestPeriod = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const financialViewStorageKey = (tenantId) =>
  `monthlyFinancialReportsActiveView:${tenantId || "default"}`;

export default function MonthlyFinancialReportsView({
  tenantId,
  user,
  userData,
  companyOptions = [],
  projectOptions = [],
  themeMode = "light",
  canEdit = true,
  mode = "all",
  embedded = false,
  lockedCompanyId = "",
  lockedPeriodKey = "",
  onReportSaved
}) {
  const dark = themeMode === "dark";
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const manualContextRef = useRef("");
  const [activeView, setActiveView] = useState(() => {
    if (mode === "entry") return "imports";
    if (mode === "reports") return "reports";
    try {
      const savedView = localStorage.getItem(financialViewStorageKey(tenantId));
      return ["imports", "manual", "reports"].includes(savedView) ? savedView : "reports";
    } catch (error) {
      return "reports";
    }
  });
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [periodKey, setPeriodKey] = useState(latestPeriod);
  const [busy, setBusy] = useState(false);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [message, setMessage] = useState(null);
  const [datasetKey, setDatasetKey] = useState("monthly");
  const [dimension, setDimension] = useState("periodLabel");
  const [metrics, setMetrics] = useState(FINANCIAL_DATASETS.monthly.defaultMetrics);
  const [aggregation, setAggregation] = useState("sum");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [manualData, setManualData] = useState(createEmptyFinancialReportData);

  const colors = {
    text: dark ? "#e5edf6" : "#123f50",
    muted: dark ? "#9fb0c4" : "#64748b",
    surface: dark ? "#162133" : "#fff",
    subtle: dark ? "#1e293b" : "#f8fafc",
    border: dark ? "#334155" : "#dbe2ea",
    input: dark ? "#111827" : "#fff"
  };

  const companies = useMemo(
    () => normalizeCompanies(companyOptions, userData),
    [companyOptions, userData]
  );
  const selectedCompany = companies.find((company) => company.id === selectedCompanyId) || null;
  const projects = useMemo(
    () => normalizeProjects(projectOptions, selectedCompany),
    [projectOptions, selectedCompany]
  );

  useEffect(() => {
    if (mode === "entry" && activeView === "reports") setActiveView("imports");
    if (mode === "reports" && activeView !== "reports") setActiveView("reports");
  }, [activeView, mode]);

  useEffect(() => {
    if (lockedCompanyId) setSelectedCompanyId(lockedCompanyId);
  }, [lockedCompanyId]);

  useEffect(() => {
    if (lockedPeriodKey) setPeriodKey(lockedPeriodKey);
  }, [lockedPeriodKey]);

  useEffect(() => {
    setLoadingReports(true);
    return subscribeMonthlyFinancialReports({
      tenantId,
      onData: (items) => {
        setReports(items);
        setLoadingReports(false);
      },
      onError: (error) => {
        setMessage({ type: "error", text: `Mali veriler yüklenemedi: ${error.message}` });
        setLoadingReports(false);
      }
    });
  }, [tenantId]);

  useEffect(() => {
    try {
      localStorage.setItem(financialViewStorageKey(tenantId), activeView);
    } catch (error) {
      // Tarayıcı depolaması kapalıysa seçim yalnızca mevcut oturumda korunur.
    }
  }, [activeView, tenantId]);

  useEffect(() => {
    const config = FINANCIAL_DATASETS[datasetKey];
    setDimension(config.dimensions[0]);
    setMetrics(config.defaultMetrics || Object.keys(config.metrics).slice(0, 2));
  }, [datasetKey]);

  const accessibleReports = useMemo(() => {
    if (!companies.length) return reports;
    const ids = new Set(companies.map((company) => company.id));
    return reports.filter((report) => ids.has(report.companyId));
  }, [companies, reports]);

  const existingReport = accessibleReports.find((report) =>
    report.companyId === selectedCompanyId && report.periodKey === periodKey
  );

  useEffect(() => {
    if (activeView !== "manual") return;
    const contextKey = `${selectedCompanyId}:${periodKey}:${existingReport?.id || "new"}`;
    if (manualContextRef.current === contextKey) return;
    manualContextRef.current = contextKey;
    const empty = createEmptyFinancialReportData();
    setManualData(existingReport ? recalculateFinancialReportData({
      ...empty,
      ...existingReport,
      offProject: { ...empty.offProject, ...(existingReport.offProject || {}) },
      funds: { ...empty.funds, ...(existingReport.funds || {}) }
    }) : empty);
  }, [activeView, existingReport, periodKey, selectedCompanyId]);

  const loadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await readMonthlyFinancialFile(file);
      setSelectedFile(file);
      setParsed(result);
      if (!lockedPeriodKey && result.detectedPeriod?.periodKey) setPeriodKey(result.detectedPeriod.periodKey);

      const detected = normalizeFinancialText(result.detectedCompanyName);
      const matched = companies.find((company) =>
        normalizeFinancialText(company.name) === detected ||
        normalizeFinancialText(company.shortName) === detected
      );
      if (!lockedCompanyId && matched) setSelectedCompanyId(matched.id);
      setMessage({
        type: "success",
        text: `${result.projects.length} proje ve ${result.rowCount} dolu satır başarıyla okundu.`
      });
    } catch (error) {
      setSelectedFile(null);
      setParsed(null);
      setMessage({ type: "error", text: error.message || "Excel dosyası okunamadı." });
    } finally {
      setBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    if (!busy) setIsDraggingFile(true);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFile(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFile(false);
    if (busy) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) loadFile(file);
  };

  const resetImport = () => {
    setSelectedFile(null);
    setParsed(null);
    setMessage(null);
  };

  const saveReport = async () => {
    if (!parsed || !selectedFile || !selectedCompany) {
      setMessage({ type: "error", text: "Excel dosyası, firma ve dönem seçimi zorunludur." });
      return;
    }
    if (existingReport && !window.confirm(`${selectedCompany.shortName} için ${formatFinancialPeriod(periodKey)} kaydı zaten var. Üzerine yazılsın mı?`)) return;

    setBusy(true);
    setMessage(null);
    try {
      await saveMonthlyFinancialReport({
        tenantId,
        company: selectedCompany,
        periodKey,
        periodLabel: formatFinancialPeriod(periodKey),
        parsed,
        file: selectedFile,
        user
      });
      setMessage({ type: "success", text: `${selectedCompany.shortName} · ${formatFinancialPeriod(periodKey)} mali verileri kaydedildi.` });
      setSelectedFile(null);
      setParsed(null);
      onReportSaved?.();
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Mali veriler kaydedilemedi." });
    } finally {
      setBusy(false);
    }
  };

  const saveManualReport = async () => {
    if (!selectedCompany || !periodKey) {
      setMessage({ type: "error", text: "Firma ve dönem seçimi zorunludur." });
      return;
    }
    const unlinkedProject = (manualData.projects || []).some((project) => !project.projectId);
    const unlinkedTrade = (manualData.trade || []).some((record) =>
      record.scope !== "offProject" &&
      !record.projectId &&
      (record.projectName || record.projectCode || Number(record.projectImport) || Number(record.projectExport))
    );
    const unlinkedFund = (manualData.funds?.records || []).some((record) => !record.projectId);
    if (unlinkedProject || unlinkedTrade || unlinkedFund) {
      setMessage({ type: "error", text: "Proje içeren satırlarda sistemde kayıtlı proje seçimi zorunludur. Proje dışı dış ticaret kaydında proje seçimini boş bırakabilirsiniz." });
      return;
    }
    if (existingReport && !window.confirm(`${selectedCompany.shortName} için ${formatFinancialPeriod(periodKey)} kaydı zaten var. Üzerine yazılsın mı?`)) return;
    setBusy(true);
    setMessage(null);
    try {
      const normalized = recalculateFinancialReportData({
        ...manualData,
        detectedCompanyName: selectedCompany.name,
        sourceSheetName: "Elle Veri Girişi"
      });
      await saveMonthlyFinancialReport({
        tenantId,
        company: selectedCompany,
        periodKey,
        periodLabel: formatFinancialPeriod(periodKey),
        parsed: normalized,
        file: null,
        user
      });
      setManualData(createEmptyFinancialReportData());
      setMessage({ type: "success", text: `${selectedCompany.shortName} · ${formatFinancialPeriod(periodKey)} mali verileri kaydedildi.` });
      onReportSaved?.();
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Mali veriler kaydedilemedi." });
    } finally {
      setBusy(false);
    }
  };

  const editReportManually = (report) => {
    setSelectedCompanyId(report.companyId || "");
    setPeriodKey(report.periodKey || latestPeriod());
    setManualData(recalculateFinancialReportData({
      ...createEmptyFinancialReportData(),
      ...report,
      offProject: { ...createEmptyFinancialReportData().offProject, ...(report.offProject || {}) },
      funds: { ...createEmptyFinancialReportData().funds, ...(report.funds || {}) }
    }));
    setActiveView("manual");
    setMessage(null);
  };

  const removeReport = async (report) => {
    if (!window.confirm(`${report.companyShortName || report.companyName} · ${report.periodLabel} kaydı silinsin mi?`)) return;
    setBusy(true);
    try {
      await deleteMonthlyFinancialReport({ tenantId, report });
      setMessage({ type: "success", text: "Mali veri kaydı ve kaynak dosyası silindi." });
    } catch (error) {
      setMessage({ type: "error", text: error.message || "Kayıt silinemedi." });
    } finally {
      setBusy(false);
    }
  };

  const reportRows = useMemo(() => {
    const filteredReports = accessibleReports.filter((report) =>
      (companyFilter === "all" || report.companyId === companyFilter) &&
      (!periodStart || report.periodKey >= periodStart) &&
      (!periodEnd || report.periodKey <= periodEnd)
    );
    return buildFinancialDatasetRows(filteredReports, datasetKey);
  }, [accessibleReports, companyFilter, datasetKey, periodEnd, periodStart]);

  const pivot = useMemo(() => createFinancialPivot({
    rows: reportRows,
    dimension,
    metrics,
    aggregation
  }), [aggregation, dimension, metrics, reportRows]);

  const dataset = FINANCIAL_DATASETS[datasetKey];

  const toggleMetric = (metric) => {
    setMetrics((current) => current.includes(metric)
      ? current.filter((item) => item !== metric)
      : [...current, metric]
    );
  };

  const exportPivot = () => {
    if (!pivot.length || !metrics.length) return;
    const rows = pivot.map((row) => ({
      [FINANCIAL_DIMENSION_LABELS[dimension]]: row.label,
      ...Object.fromEntries(metrics.map((metric) => [dataset.metrics[metric], row[metric]]))
    }));
    const sheet = XLSX.utils.json_to_sheet(rows);
    sheet["!cols"] = [{ wch: 34 }, ...metrics.map(() => ({ wch: 24 }))];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "Rapor");
    XLSX.writeFile(workbook, `Argevio-Mali-Rapor-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const mismatch = parsed && selectedCompany &&
    normalizeFinancialText(parsed.detectedCompanyName) !== normalizeFinancialText(selectedCompany.name) &&
    normalizeFinancialText(parsed.detectedCompanyName) !== normalizeFinancialText(selectedCompany.shortName);

  return (
    <section style={{ color: colors.text }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!embedded && <span style={{ width: 48, height: 48, borderRadius: 15, display: "grid", placeItems: "center", color: "#fff", background: "linear-gradient(135deg,#124254,#0f766e)" }}>
            <BarChart3 size={25}/>
          </span>}
          <div>
            <h2 style={{ margin: 0, fontSize: embedded ? 19 : undefined }}>{mode === "reports" ? "Mali Raporlar" : embedded ? "Mali veri hazırlığı" : "Aylık Mali Veriler"}</h2>
            <p style={{ margin: "5px 0 0", color: colors.muted, fontWeight: 650 }}>
              {mode === "reports"
                ? "Vio Operatör'de hazırlanan mali verilerden esnek raporlar oluşturun"
                : "Excel aktarın veya sistemde kayıtlı projeleri seçerek mali verileri elle hazırlayın"}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, padding: 4, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 13 }}>
          {mode !== "reports" && <TabButton active={activeView === "imports"} onClick={() => setActiveView("imports")}><FileSpreadsheet size={16}/> Excel Yükle</TabButton>}
          {mode !== "reports" && <TabButton active={activeView === "manual"} onClick={() => setActiveView("manual")}><FilePenLine size={16}/> Elle Giriş</TabButton>}
          {mode !== "entry" && <TabButton active={activeView === "reports"} onClick={() => setActiveView("reports")}><BarChart3 size={16}/> Rapor Oluştur</TabButton>}
        </div>
      </header>

      {message && (
        <div style={{ marginBottom: 15, padding: "12px 14px", borderRadius: 13, fontWeight: 750, background: message.type === "error" ? "#fee2e2" : "#dcfce7", color: message.type === "error" ? "#991b1b" : "#166534" }}>
          {message.text}
        </div>
      )}

      {activeView === "imports" ? (
        <>
          <div style={{ display: "grid", gridTemplateColumns: parsed ? "minmax(300px,.9fr) minmax(360px,1.1fr)" : "1fr", gap: 16, alignItems: "stretch" }}>
            <div style={panelStyle(colors)}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 13 }}>
                <UploadCloud size={21}/><strong style={{ fontSize: 17 }}>Yeni Excel aktarımı</strong>
              </div>
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" hidden onChange={(event) => loadFile(event.target.files?.[0])}/>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                disabled={busy}
                aria-label="Aylık Excel Bilgi Formu seçin veya sürükleyip bırakın"
                style={{
                  width: "100%",
                  minHeight: 150,
                  border: `2px dashed ${isDraggingFile ? "#14b8a6" : colors.border}`,
                  borderRadius: 16,
                  background: isDraggingFile ? (dark ? "#134e4a" : "#ecfdf5") : colors.subtle,
                  color: colors.text,
                  cursor: busy ? "wait" : "pointer",
                  display: "grid",
                  placeItems: "center",
                  padding: 20,
                  transform: isDraggingFile ? "scale(1.01)" : "scale(1)",
                  boxShadow: isDraggingFile ? "0 0 0 4px rgba(20,184,166,.12)" : "none",
                  transition: "border-color .18s ease, background .18s ease, transform .18s ease, box-shadow .18s ease"
                }}
              >
                <span style={{ display: "grid", placeItems: "center", gap: 8 }}>
                  {busy ? <Loader2 size={30} className="spin"/> : <FileSpreadsheet size={34} color="#0f766e"/>}
                  <strong>{isDraggingFile ? "Excel dosyasını buraya bırakın" : (selectedFile?.name || "Aylık Excel Bilgi Formu’nu seçin")}</strong>
                  <small style={{ color: colors.muted }}>{isDraggingFile ? "Dosya bırakıldığında otomatik okunacak" : "Tıklayın veya sürükleyip bırakın · .xlsx / .xls · en fazla 15 MB"}</small>
                </span>
              </button>
              {!parsed && <p style={{ color: colors.muted, fontSize: 13, lineHeight: 1.55, margin: "12px 0 0" }}>Dosya tarayıcıda doğrulanır. Firma ve dönem onayınızdan sonra kaynak Excel ile birlikte merkezi ve tenant ayrımlı olarak saklanır.</p>}
            </div>

            {parsed && (
              <div style={panelStyle(colors)}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
                  <div><strong style={{ fontSize: 17 }}>Aktarım bilgileri</strong><div style={{ marginTop: 4, color: colors.muted, fontSize: 13 }}>{parsed.sourceSheetName} sayfası</div></div>
                  <button type="button" onClick={resetImport} style={iconButton(colors)} title="Dosyayı kaldır"><RefreshCw size={16}/></button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12 }}>
                  <label style={labelStyle}>Firma<select disabled={Boolean(lockedCompanyId)} value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)} style={inputStyle(colors)}><option value="">Firma seçin</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.shortName}</option>)}</select></label>
                  <label style={labelStyle}>Rapor dönemi<input disabled={Boolean(lockedPeriodKey)} type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} style={inputStyle(colors)}/></label>
                </div>
                <div style={{ marginTop: 12, padding: 12, borderRadius: 13, background: mismatch ? "#fff7ed" : colors.subtle, border: `1px solid ${mismatch ? "#fdba74" : colors.border}` }}>
                  <div style={{ fontSize: 12, color: colors.muted, fontWeight: 800 }}>Excel’de algılanan firma</div>
                  <strong style={{ display: "block", marginTop: 4 }}>{parsed.detectedCompanyName || "Belirlenemedi"}</strong>
                  {mismatch && <small style={{ display: "block", marginTop: 6, color: "#9a3412", fontWeight: 750 }}>Seçilen firma ile Excel’deki firma adı farklı. Kaydetmeden önce kontrol edin.</small>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 8, marginTop: 12 }}>
                  <MiniMetric label="Proje" value={parsed.projects.length} colors={colors}/>
                  <MiniMetric label="Proje geliri" value={compactMoney.format(parsed.summary.projectRevenue || 0)} colors={colors}/>
                  <MiniMetric label="Proje gideri" value={compactMoney.format(parsed.summary.projectExpense || 0)} colors={colors}/>
                </div>
                <button type="button" onClick={saveReport} disabled={busy || !selectedCompanyId || !periodKey} style={{ ...primaryButton, width: "100%", marginTop: 14, opacity: busy || !selectedCompanyId || !periodKey ? .55 : 1 }}>
                  {busy ? <Loader2 size={17}/> : <CheckCircle2 size={17}/>} {existingReport ? "Mevcut dönemi güncelle" : "Firma mali verisini kaydet"}
                </button>
              </div>
            )}
          </div>

          <div style={{ ...panelStyle(colors), marginTop: 16, padding: 0, overflow: "hidden" }}>
            <div style={{ padding: "15px 17px", borderBottom: `1px solid ${colors.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><strong style={{ fontSize: 17 }}>Kayıtlı dönemler</strong><div style={{ color: colors.muted, fontSize: 12, marginTop: 3 }}>{accessibleReports.length} firma-dönem kaydı</div></div>
              {loadingReports && <Loader2 size={18}/>} 
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                <thead><tr>{["Firma", "Dönem", "Proje", "Proje Geliri", "Proje Gideri", "Kaynak Dosya", "İşlem"].map((header) => <th key={header} style={thStyle(colors)}>{header}</th>)}</tr></thead>
                <tbody>
                  {!loadingReports && !accessibleReports.length && <tr><td colSpan="7" style={{ padding: 30, textAlign: "center", color: colors.muted }}>Henüz aylık mali veri yüklenmedi.</td></tr>}
                  {accessibleReports.map((report) => (
                    <tr key={report.id}>
                      <td style={tdStyle(colors)}><strong>{report.companyShortName || report.companyName}</strong></td>
                      <td style={tdStyle(colors)}>{report.periodLabel || formatFinancialPeriod(report.periodKey)}</td>
                      <td style={tdStyle(colors)}>{report.projects?.length || 0}</td>
                      <td style={tdStyle(colors)}>{money.format(report.summary?.projectRevenue || 0)}</td>
                      <td style={tdStyle(colors)}>{money.format(report.summary?.projectExpense || 0)}</td>
                      <td style={tdStyle(colors)}>{report.sourceFile?.url ? <a href={report.sourceFile.url} target="_blank" rel="noreferrer" style={{ color: "#0f766e", fontWeight: 800, display: "inline-flex", gap: 5, alignItems: "center" }}><Download size={14}/>{report.sourceFile.name || "Excel’i aç"}</a> : "-"}</td>
                      <td style={tdStyle(colors)}>{canEdit && <span style={{ display: "flex", gap: 6 }}>{mode !== "reports" && <button type="button" onClick={() => editReportManually(report)} style={iconButton(colors)} title="Elle düzenle"><FilePenLine size={15}/></button>}<button type="button" onClick={() => removeReport(report)} style={{ ...iconButton(colors), color: "#dc2626" }} title="Kaydı sil"><Trash2 size={15}/></button></span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : activeView === "manual" ? (
        <ManualFinancialEditor
          data={manualData}
          onChange={setManualData}
          companies={companies}
          selectedCompanyId={selectedCompanyId}
          setSelectedCompanyId={setSelectedCompanyId}
          periodKey={periodKey}
          setPeriodKey={setPeriodKey}
          onSave={saveManualReport}
          busy={busy}
          existingReport={existingReport}
          colors={colors}
          canEdit={canEdit}
          projectOptions={projects}
          lockContext={Boolean(lockedCompanyId || lockedPeriodKey)}
        />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(260px,320px) minmax(0,1fr)", gap: 16, alignItems: "start" }}>
          <aside style={{ ...panelStyle(colors), position: "sticky", top: 20 }}>
            <strong style={{ fontSize: 17, display: "flex", alignItems: "center", gap: 8 }}><Plus size={18}/> Rapor alanları</strong>
            <label style={{ ...labelStyle, marginTop: 15 }}>Veri kümesi<select value={datasetKey} onChange={(event) => setDatasetKey(event.target.value)} style={inputStyle(colors)}>{Object.entries(FINANCIAL_DATASETS).map(([key, config]) => <option key={key} value={key}>{config.label}</option>)}</select></label>
            <label style={{ ...labelStyle, marginTop: 11 }}>Satır alanı<select value={dimension} onChange={(event) => setDimension(event.target.value)} style={inputStyle(colors)}>{dataset.dimensions.map((item) => <option key={item} value={item}>{FINANCIAL_DIMENSION_LABELS[item]}</option>)}</select></label>
            <label style={{ ...labelStyle, marginTop: 11 }}>Hesaplama<select value={aggregation} onChange={(event) => setAggregation(event.target.value)} style={inputStyle(colors)}><option value="sum">Toplam</option><option value="average">Ortalama</option></select></label>
            <div style={{ marginTop: 14 }}><div style={labelStyle}>Değer alanları</div><div style={{ display: "grid", gap: 7, marginTop: 7 }}>{Object.entries(dataset.metrics).map(([key, label]) => <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "8px 9px", border: `1px solid ${metrics.includes(key) ? "#5eead4" : colors.border}`, background: metrics.includes(key) ? (dark ? "#134e4a" : "#f0fdfa") : colors.subtle, borderRadius: 10, cursor: "pointer" }}><input type="checkbox" checked={metrics.includes(key)} onChange={() => toggleMetric(key)}/>{label}</label>)}</div></div>
            <div style={{ height: 1, background: colors.border, margin: "15px 0" }}/>
            <label style={labelStyle}>Firma filtresi<select value={companyFilter} onChange={(event) => setCompanyFilter(event.target.value)} style={inputStyle(colors)}><option value="all">Tüm firmalar</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.shortName}</option>)}</select></label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 11 }}><label style={labelStyle}>Başlangıç<input type="month" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} style={inputStyle(colors)}/></label><label style={labelStyle}>Bitiş<input type="month" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} style={inputStyle(colors)}/></label></div>
          </aside>

          <div style={{ display: "grid", gap: 16 }}>
            <div style={panelStyle(colors)}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}><div><strong style={{ fontSize: 17 }}>Dinamik mali rapor</strong><div style={{ color: colors.muted, fontSize: 12, marginTop: 3 }}>{reportRows.length} veri satırı · {pivot.length} grup</div></div><button type="button" onClick={exportPivot} disabled={!pivot.length || !metrics.length} style={{ ...secondaryButton(colors), opacity: !pivot.length || !metrics.length ? .5 : 1 }}><Download size={16}/> Excel indir</button></div>
              {!pivot.length || !metrics.length ? <EmptyReport colors={colors}/> : <div style={{ width: "100%", height: 340 }}><ResponsiveContainer><BarChart data={pivot} margin={{ top: 8, right: 12, left: 12, bottom: 40 }}><CartesianGrid strokeDasharray="3 3" stroke={colors.border}/><XAxis dataKey="label" interval={0} angle={-25} textAnchor="end" height={75} tick={{ fontSize: 11, fill: colors.muted }}/><YAxis tickFormatter={(value) => compactMoney.format(value)} tick={{ fontSize: 11, fill: colors.muted }}/><Tooltip formatter={(value, key) => [money.format(value), dataset.metrics[key] || key]}/><Legend formatter={(key) => dataset.metrics[key] || key}/>{metrics.map((metric, index) => <Bar key={metric} dataKey={metric} fill={CHART_COLORS[index % CHART_COLORS.length]} radius={[5, 5, 0, 0]}/>)}</BarChart></ResponsiveContainer></div>}
            </div>
            <div style={{ ...panelStyle(colors), padding: 0, overflow: "hidden" }}><div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}><thead><tr><th style={thStyle(colors)}>{FINANCIAL_DIMENSION_LABELS[dimension]}</th>{metrics.map((metric) => <th key={metric} style={thStyle(colors)}>{dataset.metrics[metric]}</th>)}</tr></thead><tbody>{pivot.map((row) => <tr key={row.label}><td style={tdStyle(colors)}><strong>{row.label}</strong></td>{metrics.map((metric) => <td key={metric} style={tdStyle(colors)}>{money.format(row[metric] || 0)}</td>)}</tr>)}{!pivot.length && <tr><td colSpan={metrics.length + 1} style={{ padding: 30, textAlign: "center", color: colors.muted }}>Seçilen filtrelerde veri bulunamadı.</td></tr>}</tbody></table></div></div>
          </div>
        </div>
      )}
    </section>
  );
}

function ManualFinancialEditor({
  data,
  onChange,
  companies,
  selectedCompanyId,
  setSelectedCompanyId,
  periodKey,
  setPeriodKey,
  onSave,
  busy,
  existingReport,
  colors,
  canEdit,
  projectOptions,
  lockContext
}) {
  const setRoot = (key, value) => onChange({ ...data, [key]: value });
  const updateList = (key, index, patch) => setRoot(key, (data[key] || []).map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  const removeList = (key, index) => setRoot(key, (data[key] || []).filter((_, itemIndex) => itemIndex !== index));
  const addProject = () => setRoot("projects", [...(data.projects || []), {
    projectId: "", status: "Devam Ediyor", projectCode: "", projectName: "", sourceLabel: "",
    revenue: { public: 0, private: 0, total: 0 },
    expense: { personnel: 0, officeRent: 0, operating: 0, universityCooperation: 0, other: 0, total: 0 }
  }]);
  const addProjectTrade = () => setRoot("trade", [...(data.trade || []), {
    scope: "project", projectId: "", projectCode: "", projectName: "", country: "", projectImport: 0, projectExport: 0, offProjectImport: 0, offProjectExport: 0
  }]);
  const addOffProjectTrade = () => setRoot("trade", [...(data.trade || []), {
    scope: "offProject", projectId: "", projectCode: "", projectName: "", country: "", projectImport: 0, projectExport: 0, offProjectImport: 0, offProjectExport: 0
  }]);
  const addFund = () => setRoot("funds", { ...(data.funds || {}), records: [...(data.funds?.records || []), {
    institutionKey: "tubitak", institutionName: "TÜBİTAK", projectId: "", projectCode: "", projectName: "", supportName: "", approvalNo: "", amount: 0
  }] });

  const updateProjectPart = (index, part, field, value) => {
    const project = data.projects[index];
    updateList("projects", index, { [part]: { ...(project[part] || {}), [field]: value } });
  };
  const updateFund = (index, patch) => setRoot("funds", {
    ...(data.funds || {}),
    records: (data.funds?.records || []).map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item)
  });
  const removeFund = (index) => setRoot("funds", {
    ...(data.funds || {}),
    records: (data.funds?.records || []).filter((_, itemIndex) => itemIndex !== index)
  });
  const selectProject = (value, apply) => {
    const project = projectOptions.find((item) => item.id === value);
    apply(project ? {
      projectId: project.id,
      projectCode: project.code,
      projectName: project.name,
      sourceLabel: project.name,
      status: project.status || "Devam Ediyor"
    } : { projectId: "", projectCode: "", projectName: "", sourceLabel: "", status: "" });
  };
  const tradeEntries = (data.trade || []).map((record, index) => ({ record, index }));
  const projectTradeEntries = tradeEntries.filter(({ record }) => record.scope !== "offProject");
  const offProjectTradeEntries = tradeEntries.filter(({ record }) => record.scope === "offProject");

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={panelStyle(colors)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
          <div><strong style={{ fontSize: 18 }}>Firma ve dönem</strong><p style={{ margin: "5px 0 0", color: colors.muted, fontSize: 13 }}>Excel kullanmadan aynı mali veri setini hazırlayın. Sıfır tutarlı bölümler portalda “Hareketim Yok” olarak işlenir.</p></div>
          {existingReport && <span style={{ padding: "7px 10px", borderRadius: 999, background: "#fef3c7", color: "#92400e", fontSize: 12, fontWeight: 850 }}>Mevcut dönem düzenleniyor</span>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 15 }}>
          <Field label="Firma" colors={colors}><select disabled={lockContext} value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)} style={inputStyle(colors)}><option value="">Firma seçin</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.shortName}</option>)}</select></Field>
          <Field label="Dönem" colors={colors}><input disabled={lockContext} type="month" value={periodKey} onChange={(event) => setPeriodKey(event.target.value)} style={inputStyle(colors)}/></Field>
        </div>
        {selectedCompanyId && !projectOptions.length && <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 11, background: "#fff7ed", color: "#9a3412", fontSize: 12, fontWeight: 750 }}>Bu firmaya bağlı aktif proje bulunamadı. Önce Projeler alanından firma bağlantısı ve STB kodunu kontrol edin.</div>}
      </div>

      <ManualSection title="Proje gelir ve giderleri" description="Her proje için kamu/özel sektör gelirini ve dört portal gider türünü girin." onAdd={addProject} colors={colors}>
        {(data.projects || []).map((project, index) => (
          <ManualRow key={index} title={`Proje ${index + 1}`} onRemove={() => removeList("projects", index)} colors={colors}>
            <ProjectField value={project} projectOptions={projectOptions} onChange={(value) => selectProject(value, (patch) => updateList("projects", index, patch))} colors={colors}/>
            <Field label="STB / proje kodu" colors={colors}><input readOnly value={project.projectCode || ""} placeholder="Projeden otomatik gelir" style={readOnlyInputStyle(colors)}/></Field>
            <Field label="Proje durumu" colors={colors}><input readOnly value={project.status || ""} placeholder="Projeden otomatik gelir" style={readOnlyInputStyle(colors)}/></Field>
            <MoneyField label="Kamu geliri" value={project.revenue?.public} onChange={(value) => updateProjectPart(index, "revenue", "public", value)} colors={colors}/>
            <MoneyField label="Özel sektör geliri" value={project.revenue?.private} onChange={(value) => updateProjectPart(index, "revenue", "private", value)} colors={colors}/>
            <MoneyField label="Personel gideri" value={project.expense?.personnel} onChange={(value) => updateProjectPart(index, "expense", "personnel", value)} colors={colors}/>
            <MoneyField label="Ofis kira gideri" value={project.expense?.officeRent} onChange={(value) => updateProjectPart(index, "expense", "officeRent", value)} colors={colors}/>
            <MoneyField label="Üniversite işbirliği gideri" value={project.expense?.universityCooperation} onChange={(value) => updateProjectPart(index, "expense", "universityCooperation", value)} colors={colors}/>
            <MoneyField label="Diğer giderler" value={project.expense?.other} onChange={(value) => updateProjectPart(index, "expense", "other", value)} colors={colors}/>
          </ManualRow>
        ))}
      </ManualSection>

      <ManualSection title="Proje dışı gelir ve gider" description="Excel’de tür bulunmadığı için gelir varsayılan olarak Özel Sektör, gider Diğer Giderler seçilir; gerekirse değiştirin." colors={colors}>
        <div style={manualGridStyle}>
          <Field label="Gelir türü" colors={colors}><select value={data.offProject?.incomeType || "Özel Sektör"} onChange={(event) => setRoot("offProject", { ...(data.offProject || {}), incomeType: event.target.value })} style={inputStyle(colors)}><option>Kamu Kuruluşu</option><option>Özel Sektör</option></select></Field>
          <MoneyField label="Proje dışı gelir" value={data.offProject?.income} onChange={(value) => setRoot("offProject", { ...(data.offProject || {}), income: value })} colors={colors}/>
          <Field label="Gider türü" colors={colors}><select value={data.offProject?.expenseType || "Diğer Giderler"} onChange={(event) => setRoot("offProject", { ...(data.offProject || {}), expenseType: event.target.value })} style={inputStyle(colors)}><option>Diğer Giderler</option></select></Field>
          <MoneyField label="Proje dışı gider" value={data.offProject?.expense} onChange={(value) => setRoot("offProject", { ...(data.offProject || {}), expense: value })} colors={colors}/>
        </div>
      </ManualSection>

      <ManualSection title="Firma muafiyetleri" description="KDV, personel gelir vergisi ve SGK işveren hissesi için matrah, hesaplanan tutar ve muafiyeti girin." colors={colors}>
        <div style={{ overflowX: "auto" }}><table style={{ width: "100%", borderCollapse: "collapse", minWidth: 680 }}><thead><tr><th style={thStyle(colors)}>Alan</th><th style={thStyle(colors)}>KDV yazılım</th><th style={thStyle(colors)}>Personel gelir vergisi</th><th style={thStyle(colors)}>SGK işveren hissesi</th></tr></thead><tbody>{(data.exemptions || []).map((row, index) => <tr key={row.key}><td style={tdStyle(colors)}><strong>{row.label}</strong></td>{["kdvSoftware", "incomeTax", "sgkEmployer"].map((field) => <td key={field} style={tdStyle(colors)}><input type="number" step="0.01" value={row[field] ?? 0} onChange={(event) => updateList("exemptions", index, { [field]: event.target.value })} style={inputStyle(colors)}/></td>)}</tr>)}</tbody></table></div>
      </ManualSection>

      <ManualSection title="Proje dış ticaret" description="Projeye bağlı ithalat ve ihracat kayıtlarını ülke bazında ekleyin." onAdd={addProjectTrade} colors={colors}>
        {projectTradeEntries.map(({ record, index }, sectionIndex) => (
          <ManualRow key={index} title={`Proje dış ticaret ${sectionIndex + 1}`} onRemove={() => removeList("trade", index)} colors={colors}>
            <ProjectField value={record} projectOptions={projectOptions} onChange={(value) => selectProject(value, (patch) => updateList("trade", index, patch))} colors={colors}/>
            <Field label="STB / proje kodu" colors={colors}><input readOnly value={record.projectCode || ""} placeholder="Projeden otomatik gelir" style={readOnlyInputStyle(colors)}/></Field>
            <Field label="Ülke" colors={colors}><input value={record.country || ""} onChange={(event) => updateList("trade", index, { country: event.target.value })} style={inputStyle(colors)}/></Field>
            <MoneyField label="İthalat" value={record.projectImport} onChange={(value) => updateList("trade", index, { projectImport: value })} colors={colors}/>
            <MoneyField label="İhracat" value={record.projectExport} onChange={(value) => updateList("trade", index, { projectExport: value })} colors={colors}/>
          </ManualRow>
        ))}
      </ManualSection>

      <ManualSection title="Proje dışı dış ticaret" description="Herhangi bir projeye bağlı olmayan ithalat ve ihracat kayıtlarını ülke bazında ekleyin." onAdd={addOffProjectTrade} colors={colors}>
        {offProjectTradeEntries.map(({ record, index }, sectionIndex) => (
          <ManualRow key={index} title={`Proje dışı dış ticaret ${sectionIndex + 1}`} onRemove={() => removeList("trade", index)} colors={colors}>
            <Field label="Ülke" colors={colors}><input value={record.country || ""} onChange={(event) => updateList("trade", index, { country: event.target.value })} style={inputStyle(colors)}/></Field>
            <MoneyField label="İthalat" value={record.offProjectImport} onChange={(value) => updateList("trade", index, { offProjectImport: value })} colors={colors}/>
            <MoneyField label="İhracat" value={record.offProjectExport} onChange={(value) => updateList("trade", index, { offProjectExport: value })} colors={colors}/>
          </ManualRow>
        ))}
      </ManualSection>

      <ManualSection title="Ar-Ge fon destekleri" description="Her kurum desteğini proje, program, onay numarası ve tutarıyla ayrı kayıt olarak ekleyin." onAdd={addFund} colors={colors}>
        {(data.funds?.records || []).map((record, index) => (
          <ManualRow key={index} title={`Fon desteği ${index + 1}`} onRemove={() => removeFund(index)} colors={colors}>
            <Field label="Kurum" colors={colors}><select value={record.institutionKey || "tubitak"} onChange={(event) => { const option = event.target.options[event.target.selectedIndex]; updateFund(index, { institutionKey: event.target.value, institutionName: option.text }); }} style={inputStyle(colors)}><option value="tubitak">TÜBİTAK</option><option value="kosgeb">KOSGEB</option><option value="ttgv">TTGV</option><option value="eu">Avrupa Birliği</option></select></Field>
            <ProjectField value={record} projectOptions={projectOptions} onChange={(value) => selectProject(value, (patch) => updateFund(index, patch))} colors={colors}/>
            <Field label="STB / proje kodu" colors={colors}><input readOnly value={record.projectCode || ""} placeholder="Projeden otomatik gelir" style={readOnlyInputStyle(colors)}/></Field>
            <Field label="Destek programı" colors={colors}><input value={record.supportName || ""} onChange={(event) => updateFund(index, { supportName: event.target.value })} style={inputStyle(colors)}/></Field>
            <Field label="Destek onay no" colors={colors}><input value={record.approvalNo || ""} onChange={(event) => updateFund(index, { approvalNo: event.target.value })} style={inputStyle(colors)}/></Field>
            <MoneyField label="Tutar" value={record.amount} onChange={(value) => updateFund(index, { amount: value })} colors={colors}/>
          </ManualRow>
        ))}
      </ManualSection>

      <button type="button" onClick={onSave} disabled={busy || !canEdit || !selectedCompanyId || !periodKey} style={{ ...primaryButton, minHeight: 46, opacity: busy || !canEdit || !selectedCompanyId || !periodKey ? .55 : 1 }}>
        {busy ? <Loader2 size={18} className="spin"/> : <CheckCircle2 size={18}/>} {existingReport ? "Dönem mali verilerini güncelle" : "Dönem mali verilerini kaydet"}
      </button>
    </div>
  );
}

function ManualSection({ title, description, onAdd, colors, children }) {
  return <section style={panelStyle(colors)}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 13 }}><div><strong style={{ fontSize: 17 }}>{title}</strong><div style={{ color: colors.muted, fontSize: 12, marginTop: 4 }}>{description}</div></div>{onAdd && <button type="button" onClick={onAdd} style={secondaryButton(colors)}><Plus size={15}/> Kayıt ekle</button>}</div>{children || <div style={{ color: colors.muted, fontSize: 13 }}>Bu bölüm için tutar girebilirsiniz.</div>}</section>;
}

function ManualRow({ title, onRemove, colors, children }) {
  return <div style={{ padding: 13, border: `1px solid ${colors.border}`, borderRadius: 14, background: colors.subtle, marginTop: 10 }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}><strong style={{ fontSize: 13 }}>{title}</strong><button type="button" onClick={onRemove} style={{ ...iconButton(colors), color: "#dc2626", width: 30, height: 30 }}><Trash2 size={14}/></button></div><div style={manualGridStyle}>{children}</div></div>;
}

function Field({ label, children }) {
  return <label style={labelStyle}>{label}{children}</label>;
}

function ProjectField({ value, projectOptions, onChange, colors, allowEmpty = false }) {
  const matched = projectOptions.find((project) =>
    project.id === value.projectId ||
    (value.projectCode && normalizeFinancialText(project.code) === normalizeFinancialText(value.projectCode)) ||
    (value.projectName && normalizeFinancialText(project.name) === normalizeFinancialText(value.projectName))
  );
  const legacyValue = !matched && (value.projectName || value.projectCode) ? "__unlinked__" : "";
  return (
    <Field label="Sistem projesi">
      <select value={matched?.id || legacyValue} onChange={(event) => onChange(event.target.value)} style={inputStyle(colors)}>
        <option value="">{allowEmpty ? "Proje dışı işlem" : "Proje seçin"}</option>
        {legacyValue && <option value="__unlinked__" disabled>Eşleşmemiş: {value.projectCode ? `${value.projectCode} · ` : ""}{value.projectName}</option>}
        {projectOptions.map((project) => <option key={project.id} value={project.id}>{project.code ? `${project.code} · ` : ""}{project.name}</option>)}
      </select>
    </Field>
  );
}

function MoneyField({ label, value, onChange, colors }) {
  return <Field label={label}><input type="number" step="0.01" min="0" value={value ?? 0} onChange={(event) => onChange(event.target.value)} style={inputStyle(colors)}/></Field>;
}

const manualGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10 };

function TabButton({ active, onClick, children }) {
  return <button type="button" onClick={onClick} style={{ border: 0, borderRadius: 9, padding: "9px 12px", display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: 850, background: active ? "#0f766e" : "transparent", color: active ? "#fff" : "#64748b" }}>{children}</button>;
}

function MiniMetric({ label, value, colors }) {
  return <div style={{ padding: 10, borderRadius: 11, background: colors.subtle, border: `1px solid ${colors.border}` }}><div style={{ fontSize: 11, color: colors.muted, fontWeight: 750 }}>{label}</div><strong style={{ display: "block", marginTop: 4, fontSize: 14 }}>{value}</strong></div>;
}

function EmptyReport({ colors }) {
  return <div style={{ minHeight: 260, display: "grid", placeItems: "center", textAlign: "center", color: colors.muted }}><div><BarChart3 size={38} style={{ opacity: .45 }}/><strong style={{ display: "block", marginTop: 8, color: colors.text }}>Rapor için alan seçin</strong><span style={{ display: "block", marginTop: 4, fontSize: 13 }}>En az bir değer alanı ve kayıtlı dönem gereklidir.</span></div></div>;
}

const panelStyle = (colors) => ({ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 18, padding: 17, boxShadow: "0 10px 30px rgba(15,23,42,.04)" });
const labelStyle = { display: "grid", gap: 6, fontSize: 12, fontWeight: 850 };
const inputStyle = (colors) => ({ width: "100%", boxSizing: "border-box", padding: "10px 11px", borderRadius: 10, border: `1px solid ${colors.border}`, background: colors.input, color: colors.text, font: "inherit" });
const readOnlyInputStyle = (colors) => ({ ...inputStyle(colors), background: colors.subtle, color: colors.muted, cursor: "default" });
const primaryButton = { border: 0, borderRadius: 11, padding: "11px 14px", background: "#0f766e", color: "#fff", fontWeight: 850, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, cursor: "pointer" };
const secondaryButton = (colors) => ({ border: `1px solid ${colors.border}`, borderRadius: 10, padding: "9px 11px", background: colors.surface, color: colors.text, fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" });
const iconButton = (colors) => ({ width: 34, height: 34, borderRadius: 9, border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text, display: "grid", placeItems: "center", cursor: "pointer" });
const thStyle = (colors) => ({ textAlign: "left", padding: "11px 13px", borderBottom: `1px solid ${colors.border}`, color: colors.muted, background: colors.subtle, fontSize: 12, whiteSpace: "nowrap" });
const tdStyle = (colors) => ({ padding: "12px 13px", borderBottom: `1px solid ${colors.border}`, fontSize: 13, verticalAlign: "middle" });
